// src/pages/educator/EducatorQuizEditor.js
//
// Lets an educator write their own quiz questions for a topic.
//
// ===========================================================================
// THE CRITICAL DESIGN DECISION: WHERE THESE WRITE TO
// ===========================================================================
// The obvious implementation is to reuse what QuizManager already does:
//
//     setDoc(doc(db, "quizzes", slug), { questions })
//
// DO NOT DO THAT. `quizzes/{slug}` is the GLOBAL library — the document ID is
// just the topic slug, with no owner in the path. One teacher in Ohio editing
// "Rounding" would overwrite the canonical Rounding quiz for every school in
// the country, silently, with no way to tell whose version you're looking at.
//
// Educator content goes to a path that includes the owner:
//
//     educatorQuizzes/{educatorCode}/topics/{slug}
//
// and games/UniversalQuizGame.js consults that path FIRST, before the global
// library (see hydrateTopicQuestions there). Net effect:
//
//   - a teacher's own class gets their questions
//   - every other class is untouched
//   - the global library stays the fallback, so a topic a teacher hasn't
//     customised still works exactly as before
//
// ===========================================================================
// MODERATION
// ===========================================================================
// Anything typed here will be read by children. Even trusted teachers typo,
// and an educator code can be shared further than intended. Submissions are
// therefore written with `status: "draft"` and are visible ONLY to that
// educator's own class until an admin promotes them. Promotion to the global
// library is a separate, deliberate admin action — see
// src/pages/admin/AdminQuizReview.js.
//
// The licence that lets you publish educator questions commercially is
// Section 8 of the Terms. It is re-confirmed on this screen rather than
// relied on from signup, because a person submitting content should be told
// what happens to it at the moment they submit it.

import React, { useState, useEffect, useCallback } from "react";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useParams, useNavigate, Link } from "react-router-dom";

const MAX_QUESTIONS = 20;
const MIN_ANSWERS = 2;
const MAX_ANSWERS = 4;

const blankQuestion = () => ({
  question: "",
  answers: [
    { text: "", correct: true },
    { text: "", correct: false },
    { text: "", correct: false },
    { text: "", correct: false },
  ],
});

const titleCase = (s = "") =>
  String(s).replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

export default function EducatorQuizEditor() {
  const db = getFirestore();
  const auth = getAuth();
  const navigate = useNavigate();
  const { subject, slug } = useParams();

  const [educator, setEducator] = useState(null);
  const [questions, setQuestions] = useState([blankQuestion()]);
  const [status, setStatus] = useState("draft");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [shareConsent, setShareConsent] = useState(false);

  // -------------------------------------------------------------------------

  const load = useCallback(
    async (uid) => {
      try {
        const meSnap = await getDoc(doc(db, "users", uid));
        const me = meSnap.exists() ? { uid, ...meSnap.data() } : null;
        setEducator(me);

        if (!me?.educatorCode) {
          setError(
            "Your educator code hasn't been issued yet. Open your dashboard once and it will be created."
          );
          setLoading(false);
          return;
        }

        const existing = await getDoc(
          doc(db, "educatorQuizzes", String(me.educatorCode), "topics", slug)
        );

        if (existing.exists()) {
          const data = existing.data();
          if (Array.isArray(data.questions) && data.questions.length) {
            setQuestions(data.questions);
          }
          setStatus(data.status || "draft");
          setShareConsent(Boolean(data.shareConsent));
        }
      } catch (err) {
        console.error("[EducatorQuizEditor] load failed:", err);
        setError("We couldn't load this quiz. Please refresh and try again.");
      } finally {
        setLoading(false);
      }
    },
    [db, slug]
  );

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) load(u.uid);
      else setLoading(false);
    });
    return () => unsub();
  }, [auth, load]);

  // -------------------------------------------------------------------------
  // Editing
  // -------------------------------------------------------------------------

  const updateQuestion = (qi, text) =>
    setQuestions((prev) =>
      prev.map((q, i) => (i === qi ? { ...q, question: text } : q))
    );

  const updateAnswer = (qi, ai, text) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i !== qi
          ? q
          : {
              ...q,
              answers: q.answers.map((a, j) =>
                j === ai ? { ...a, text } : a
              ),
            }
      )
    );

  // Exactly one correct answer, enforced by construction rather than by
  // validation — picking a new correct answer clears the old one, so an
  // invalid state is unreachable.
  const markCorrect = (qi, ai) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i !== qi
          ? q
          : {
              ...q,
              answers: q.answers.map((a, j) => ({ ...a, correct: j === ai })),
            }
      )
    );

  const addQuestion = () =>
    setQuestions((prev) =>
      prev.length >= MAX_QUESTIONS ? prev : [...prev, blankQuestion()]
    );

  const removeQuestion = (qi) =>
    setQuestions((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== qi)
    );

  // -------------------------------------------------------------------------

  const validate = () => {
    const cleaned = questions
      .map((q) => ({
        question: q.question.trim(),
        answers: q.answers
          .map((a) => ({ text: a.text.trim(), correct: Boolean(a.correct) }))
          .filter((a) => a.text),
      }))
      .filter((q) => q.question);

    if (cleaned.length === 0) return { error: "Add at least one question." };

    for (let i = 0; i < cleaned.length; i += 1) {
      const q = cleaned[i];
      if (q.answers.length < MIN_ANSWERS) {
        return { error: `Question ${i + 1} needs at least two answer choices.` };
      }
      if (!q.answers.some((a) => a.correct)) {
        return { error: `Question ${i + 1} needs a correct answer selected.` };
      }
    }

    return { cleaned };
  };

  const handleSave = async (nextStatus) => {
    setError("");
    setMessage("");

    const { error: problem, cleaned } = validate();
    if (problem) {
      setError(problem);
      return;
    }

    if (nextStatus === "submitted" && !shareConsent) {
      setError(
        "Please confirm you wrote these questions and agree to the sharing terms."
      );
      return;
    }

    setSaving(true);
    try {
      await setDoc(
        doc(
          db,
          "educatorQuizzes",
          String(educator.educatorCode),
          "topics",
          slug
        ),
        {
          slug,
          subject: subject || "",
          title: titleCase(slug),
          gameType: "multipleChoice",
          questions: cleaned,
          status: nextStatus,
          shareConsent,
          educatorCode: String(educator.educatorCode),
          educatorUid: educator.uid,
          educatorName:
            `${educator.firstName || ""} ${educator.lastName || ""}`.trim(),
          state: educator.state || "",
          grade: educator.grade || "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setStatus(nextStatus);
      setMessage(
        nextStatus === "submitted"
          ? "Saved and submitted for review. Your class sees these questions now."
          : "Saved. Your class sees these questions now."
      );
    } catch (err) {
      console.error("[EducatorQuizEditor] save failed:", err);
      setError(
        "We couldn't save your questions. Please try again, or call (571) 563-6142."
      );
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.shell}>
          <p style={s.muted}>Loading…</p>
        </div>
      </div>
    );
  }

  if (error && !educator?.educatorCode) {
    return (
      <div style={s.page}>
        <div style={s.shell}>
          <div style={s.error}>{error}</div>
          <button style={s.ghost} onClick={() => navigate("/educator/dashboard")}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.shell}>
        <header style={s.head}>
          <div>
            <p style={s.eyebrow}>Your quiz questions</p>
            <h1 style={s.h1}>{titleCase(slug)}</h1>
            <p style={s.sub}>
              These replace our questions for <strong>your class only</strong>.
              Other classes keep the standard set.
            </p>
          </div>
          {status !== "draft" && (
            <span style={s.statusPill}>
              {status === "submitted" ? "Submitted for review" : status}
            </span>
          )}
        </header>

        {questions.map((q, qi) => (
          <section key={qi} style={s.card}>
            <div style={s.cardHead}>
              <span style={s.qNum}>Question {qi + 1}</span>
              {questions.length > 1 && (
                <button
                  style={s.removeBtn}
                  onClick={() => removeQuestion(qi)}
                  aria-label={`Remove question ${qi + 1}`}
                >
                  Remove
                </button>
              )}
            </div>

            <textarea
              value={q.question}
              onChange={(e) => updateQuestion(qi, e.target.value)}
              placeholder="What do you want to ask?"
              style={s.textarea}
              rows={2}
              maxLength={300}
            />

            <p style={s.answerHint}>
              Tick the correct answer. Leave a box empty to use fewer choices.
            </p>

            {q.answers.slice(0, MAX_ANSWERS).map((a, ai) => (
              <div key={ai} style={s.answerRow}>
                <input
                  type="radio"
                  name={`correct-${qi}`}
                  checked={Boolean(a.correct)}
                  onChange={() => markCorrect(qi, ai)}
                  style={s.radio}
                  aria-label={`Mark answer ${ai + 1} correct`}
                />
                <input
                  type="text"
                  value={a.text}
                  onChange={(e) => updateAnswer(qi, ai, e.target.value)}
                  placeholder={`Answer ${ai + 1}`}
                  style={{
                    ...s.answerInput,
                    ...(a.correct ? s.answerInputCorrect : null),
                  }}
                  maxLength={160}
                />
              </div>
            ))}
          </section>
        ))}

        {questions.length < MAX_QUESTIONS && (
          <button style={s.addBtn} onClick={addQuestion}>
            + Add another question
          </button>
        )}

        {/* Consent is captured HERE, at the moment of submission, not
            inherited from signup. Section 8 of the Terms is the licence; this
            is the notice that it's being exercised. */}
        <label style={s.consentRow}>
          <input
            type="checkbox"
            checked={shareConsent}
            onChange={(e) => setShareConsent(e.target.checked)}
            style={s.checkbox}
          />
          <span style={s.consentText}>
            I wrote these questions myself. They aren't copied from a textbook,
            workbook, or test, and they contain no student names or personal
            details. I agree Smart Board Learning may review, edit, and include
            them in the wider content library, as described in{" "}
            <Link to="/terms-of-service" target="_blank" style={s.link}>
              Section 8 of the Terms
            </Link>
            .
          </span>
        </label>

        {error && <div style={s.error}>{error}</div>}
        {message && <div style={s.success}>{message}</div>}

        <div style={s.actions}>
          <button
            style={{ ...s.primary, opacity: saving ? 0.6 : 1 }}
            disabled={saving}
            onClick={() => handleSave("draft")}
          >
            {saving ? "Saving…" : "Save for my class"}
          </button>
          <button
            style={{ ...s.secondary, opacity: saving ? 0.6 : 1 }}
            disabled={saving}
            onClick={() => handleSave("submitted")}
          >
            Save &amp; share with Smart Board Learning
          </button>
        </div>

        <p style={s.footnote}>
          Sharing is optional. "Save for my class" keeps these questions
          private to your students.
        </p>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f8fafc",
    padding: "32px 20px 72px",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    color: "#0f172a",
  },
  shell: { maxWidth: 720, margin: "0 auto" },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 22,
  },
  eyebrow: {
    margin: 0,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
  },
  h1: { margin: "4px 0 6px", fontSize: 30, fontWeight: 800 },
  sub: { margin: 0, color: "#475569", fontSize: 15 },
  statusPill: {
    background: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
    borderRadius: 999,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 20,
    marginBottom: 14,
  },
  cardHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  qNum: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#64748b",
  },
  removeBtn: {
    background: "none",
    border: "none",
    color: "#b91c1c",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  textarea: {
    width: "100%",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 15,
    fontFamily: "inherit",
    resize: "vertical",
    boxSizing: "border-box",
  },
  answerHint: { fontSize: 12.5, color: "#64748b", margin: "12px 0 8px" },
  answerRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  radio: { width: 17, height: 17, flexShrink: 0, cursor: "pointer" },
  answerInput: {
    flex: 1,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 14.5,
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  answerInputCorrect: { borderColor: "#86efac", background: "#f0fdf4" },
  addBtn: {
    width: "100%",
    background: "#fff",
    border: "1px dashed #cbd5e1",
    borderRadius: 10,
    padding: "12px",
    fontWeight: 600,
    color: "#475569",
    cursor: "pointer",
    marginBottom: 20,
  },
  consentRow: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "13px 15px",
    marginBottom: 14,
    cursor: "pointer",
  },
  checkbox: { marginTop: 3, width: 17, height: 17, flexShrink: 0 },
  consentText: { fontSize: 13.5, lineHeight: 1.55, color: "#334155" },
  link: { color: "#1d4ed8", fontWeight: 600 },
  actions: { display: "flex", gap: 10, flexWrap: "wrap" },
  primary: {
    flex: "1 1 200px",
    background: "#1d4ed8",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "13px 18px",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },
  secondary: {
    flex: "1 1 200px",
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "13px 18px",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },
  ghost: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "10px 18px",
    fontWeight: 600,
    cursor: "pointer",
  },
  error: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    borderRadius: 8,
    padding: "11px 14px",
    fontSize: 14,
    marginBottom: 12,
  },
  success: {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    color: "#166534",
    borderRadius: 8,
    padding: "11px 14px",
    fontSize: 14,
    marginBottom: 12,
  },
  footnote: { fontSize: 13, color: "#64748b", marginTop: 14, textAlign: "center" },
  muted: { color: "#64748b" },
};
